import { useState } from 'react';
import { Popover } from '@base-ui/react/popover';
import { Checkbox } from '@components/ui/Checkbox/Checkbox';
import { TradePill } from '@components/ui/TradePill/TradePill';
import type { IGroupedOptions } from '@components/Pages/AnalyticsPage/analytics.types';
import chevronDownSvg from '@assets/analytics/chevron-down.svg?raw';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './MultiSelect.module.css';

interface IMultiSelectProps {
  value: string[];
  onChange: (next: string[]) => void;
  groups: IGroupedOptions[];
  placeholder: string;
  ariaLabel: string;
  disabled?: boolean;
  /** Set when rendered inside a modal dialog: lifts the portaled popup above
   *  the dialog layer, which otherwise hides it (same as SingleSelect). */
  elevated?: boolean;
}

/**
 * Bucket consecutive inner-groups that share the same `topLevelGroupId`
 * into a single top-level row. Groups without `topLevelGroupId` are each
 * wrapped in their own anonymous bucket (no top-level header rendered) so
 * single-level callers stay flat. Matches Trusstor's
 * `MultiTagsGroupedByTradeDropdown` two-level structure
 * (tradeGroup → profession → tags).
 */
function bucketByTopLevel(
  groups: IGroupedOptions[],
): { id: string; label?: string; groups: IGroupedOptions[] }[] {
  const buckets: { id: string; label?: string; groups: IGroupedOptions[] }[] = [];
  let i = 0;
  while (i < groups.length) {
    const head = groups[i]!;
    if (head.topLevelGroupId === undefined) {
      // Flat passthrough — single-level caller. Each group is its own bucket
      // so the keys stay unique without leaking a synthetic id.
      buckets.push({ id: `flat:${head.groupId}`, groups: [head] });
      i += 1;
      continue;
    }
    const groupId = head.topLevelGroupId;
    const label = head.topLevelGroupLabel;
    const slice: IGroupedOptions[] = [];
    while (i < groups.length && groups[i]!.topLevelGroupId === groupId) {
      slice.push(groups[i]!);
      i += 1;
    }
    buckets.push({ id: `top:${groupId}`, ...(label ? { label } : {}), groups: slice });
  }
  return buckets;
}

export function MultiSelect({
  value,
  onChange,
  groups,
  placeholder,
  ariaLabel,
  disabled,
  elevated,
}: IMultiSelectProps) {
  const selected = new Set(value);
  const total = value.length;
  // Collapsed state per inner group — keyed by `groupId`. A group is
  // `expanded` when its id is in the set; otherwise the chip renders
  // alone and the tag rows are hidden. Initial value is "all collapsed"
  // so the picker stays compact (Trusstor's UX).
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (groupId: string) => {
    const next = new Set(expanded);
    if (next.has(groupId)) next.delete(groupId);
    else next.add(groupId);
    setExpanded(next);
  };

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  /**
   * Select-all toggle for an inner group: clears every option in the
   * group when ALL are currently selected, otherwise adds every option.
   * Matches the behaviour of common bulk-select pickers — clicking on a
   * partially-selected state fills it, clicking on a fully-selected
   * state empties it.
   */
  const toggleGroup = (group: IGroupedOptions) => {
    const ids = group.options.map((o) => o.id);
    const allSelected = ids.every((id) => selected.has(id));
    const next = new Set(selected);
    if (allSelected) {
      for (const id of ids) next.delete(id);
    } else {
      for (const id of ids) next.add(id);
    }
    onChange(Array.from(next));
  };

  return (
    <Popover.Root>
      <Popover.Trigger
        type="button"
        aria-label={ariaLabel}
        className={styles.trigger}
        disabled={disabled}
      >
        <span className={styles.value}>
          {total > 0 ? (
            <span className={styles.count}>{total}</span>
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </span>
        <span
          className={styles.icon}
          aria-hidden="true"
          dangerouslySetInnerHTML={{ __html: chevronDownSvg }}
        />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner
          sideOffset={4}
          align="start"
          collisionPadding={8}
          className={elevated ? styles.positioner : undefined}
        >
          <Popover.Popup className={styles.popup}>
            <ScrollArea>
              {bucketByTopLevel(groups).map((bucket) => (
                <div key={bucket.id} className={styles.topLevelGroup}>
                  {bucket.label && (
                    <div className={styles.topLevelHeader}>{bucket.label}</div>
                  )}
                  {bucket.groups.map((group) => {
                    // Collapsible inner group: render a clickable chip
                    // header with optional bg colour + icon + count, hide
                    // tag rows until the user expands. Selected count in
                    // the chip helps the user know which professions have
                    // picks without expanding.
                    const isCollapsible = group.isCollapsible === true;
                    const isOpen = !isCollapsible || expanded.has(group.groupId);
                    const selectedCount = group.options.reduce(
                      (n, o) => (selected.has(o.id) ? n + 1 : n),
                      0,
                    );
                    const allInGroupSelected =
                      group.options.length > 0 &&
                      selectedCount === group.options.length;
                    return (
                      <div key={group.groupId} className={styles.group}>
                        {isCollapsible ? (
                          // Chip row: a select-all `Checkbox`, the trade
                          // pill (clickable → expands), the count, and the
                          // expand chevron. Each control owns its own
                          // event so the checkbox can flip the whole group
                          // without also toggling expand/collapse.
                          <div className={styles.chipRow}>
                            <Checkbox
                              checked={allInGroupSelected}
                              onCheckedChange={() => toggleGroup(group)}
                              ariaLabel={`Select all in ${group.groupLabel}`}
                            />
                            <button
                              type="button"
                              className={styles.chipExpand}
                              onClick={() => toggleExpand(group.groupId)}
                              aria-expanded={isOpen}
                            >
                              {/* Shared `<TradePill>` — same component used by
                                  MonitorPage WorkforceTab + IssuesPage
                                  ProfessionsFilter. Count sibling avoids the
                                  pill's max-width clip. */}
                              <span className={styles.chipGroup}>
                                <TradePill
                                  label={group.groupLabel}
                                  {...(group.groupColor ? { backgroundColor: group.groupColor } : {})}
                                  {...(group.groupTradeId ? { tradeId: group.groupTradeId } : {})}
                                />
                                <span className={styles.chipCount}>
                                  {selectedCount > 0 ? `${selectedCount}/${group.options.length}` : group.options.length}
                                </span>
                              </span>
                              <span
                                className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
                                aria-hidden="true"
                                dangerouslySetInnerHTML={{ __html: chevronDownSvg }}
                              />
                            </button>
                          </div>
                        ) : (
                          <div className={styles.groupHeader}>{group.groupLabel}</div>
                        )}
                        {isOpen && group.options.map((opt) => (
                          <div
                            key={opt.id}
                            className={styles.row}
                            onClick={() => toggle(opt.id)}
                            role="presentation"
                          >
                            <Checkbox
                              checked={selected.has(opt.id)}
                              onCheckedChange={() => toggle(opt.id)}
                              ariaLabel={opt.label}
                            />
                            <span className={styles.rowLabel}>{opt.label}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </ScrollArea>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
